import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BarChart3,
  Brain,
  Target,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Search,
  Settings,
  Lightbulb,
  MapPin,
  Clock,
  Mail,
  Zap,
  Users,
  Cog,
  LineChart,
  ShoppingCart,
  Building,
  HelpCircle,
  ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import CookieBanner from "@/components/CookieBanner";

const WHATSAPP_URL = "https://wa.me/34672972514?text=Hola%2C%20necesito%20un%20consultor%20de%20datos%20para%20mi%20empresa%20en%20Murcia";
const LOGO_URL = "/datyon-logo.png";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const FAQS = [
  {
    q: "¿Qué diferencia hay entre un consultor de datos en Murcia y una asesoría informática?",
    a: "Una asesoría informática se centra en sistemas y software; un consultor de datos en Murcia se centra en negocio. Mi trabajo no es montar servidores, sino traducir tus números en decisiones concretas que mejoren la rentabilidad."
  },
  {
    q: "¿Tengo que tener los datos ordenados antes de contactar contigo?",
    a: "No. Los datos llegan como llegan: hojas de cálculo, ERP, CRM, exportaciones manuales. Una de las primeras tareas como consultor de datos es precisamente ordenarlos y unificarlos antes de extraer conclusiones útiles."
  },
  {
    q: "¿Cuánto tiempo tarda un consultor de datos en darme las primeras conclusiones útiles?",
    a: "En la mayoría de proyectos los primeros hallazgos accionables están sobre la mesa en 1-2 semanas. No trabajo con consultorías largas: cada semana tienes algo concreto que aplicar al negocio."
  },
  {
    q: "¿Trabajas presencialmente con empresas de Murcia o todo se hace en remoto?",
    a: "Las dos modalidades. Con empresas de Murcia ciudad y alrededores hago kickoff y reuniones clave en persona; el desarrollo del análisis es remoto. Con clientes fuera de la Región todo se hace por videollamada."
  },
  {
    q: "¿Firmas acuerdo de confidencialidad antes de acceder a mis datos?",
    a: "Sí, siempre. Antes de acceder a cualquier base de datos firmamos un NDA. La protección de tu información comercial y financiera es innegociable y queda formalizada por escrito."
  },
  {
    q: "¿Qué pasa si tras el diagnóstico decido no seguir adelante?",
    a: "Sin compromiso. La sesión inicial y el diagnóstico están pensados para que decidas con información en la mano si continuamos o no. Si no aporto valor real para tu empresa, lo digo yo primero."
  }
];

export default function ConsultorDatosMurcia() {
  useEffect(() => {
    document.title = "Analista de Datos en Murcia | Consultor de datos para empresas | Datyon";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Analista de datos en Murcia especializado en ayudar a empresas a detectar dónde pierden dinero y mejorar su rentabilidad. Consultor de datos con enfoque práctico. Diagnóstico gratuito.");
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", "https://datyon.com/consultor-datos-murcia");
    }

    // FAQPage JSON-LD para rich snippets en Google
    const ldScript = document.createElement("script");
    ldScript.type = "application/ld+json";
    ldScript.id = "consultor-faq-jsonld";
    ldScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a }
      }))
    });
    document.head.appendChild(ldScript);

    window.scrollTo(0, 0);
    return () => {
      document.title = "Análisis de Datos en Murcia para Empresas | Datyon";
      if (metaDesc) metaDesc.setAttribute("content", "Transforma los datos de tu empresa en decisiones rentables. Servicios de análisis de datos, business intelligence y modelos predictivos en Murcia.");
      if (canonical) canonical.setAttribute("href", "https://datyon.com/");
      const existing = document.getElementById("consultor-faq-jsonld");
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#020617] relative overflow-x-hidden" data-testid="consultor-page">
      <div className="noise-overlay" />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="consultor-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center pl-4" data-testid="consultor-nav-logo">
              <img src={LOGO_URL} alt="DATYON - Consultor de Datos en Murcia" className="h-20 w-auto" style={{ maxHeight: '80px' }} />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Inicio</Link>
              <Link to="/servicios" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Servicios</Link>
              <Link to="/proceso" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Proceso</Link>
              <Link to="/sobre-mi" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Sobre mí</Link>
              <Link to="/blog" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Blog</Link>
              <Link to="/contacto" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Contacto</Link>
            </div>
          </div>
        </div>
      </nav>

      <main>
        {/* Hero */}
        <section className="relative min-h-[80vh] flex items-center section-padding pt-24 overflow-hidden" data-testid="consultor-hero">
          <div className="spotlight-1 -top-40 -left-40" />
          <div className="spotlight-2 top-1/3 right-0" />
          <div className="max-w-7xl mx-auto w-full relative z-10">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-3xl">
              <motion.div variants={fadeInUp} className="flex items-center gap-2 mb-4">
                <MapPin className="w-4 h-4 text-indigo-400" />
                <p className="text-indigo-400 font-medium text-sm md:text-base">
                  Datyon — Murcia, España
                </p>
              </motion.div>
              <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6" data-testid="consultor-h1">
                Analista de datos en Murcia{" "}
                <span className="text-gradient">para empresas que quieren mejorar su rentabilidad</span>
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-slate-400 text-base md:text-lg mb-8 max-w-2xl">
                Soy consultor de datos en Murcia y ayudo a empresas a entender qué está pasando
                realmente en su negocio: dónde pierden dinero, qué funciona y qué no, y qué decisiones
                tomar para mejorar resultados. Sin informes que nadie lee. Sin proyectos interminables.
                Solo datos convertidos en decisiones claras.
              </motion.p>
              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
                <Button asChild className="btn-glow bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-base rounded-full" data-testid="consultor-hero-cta">
                  <Link to="/contacto">
                    Solicita tu revisión gratuita
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-base rounded-full transition-all hover:scale-105 hover:shadow-lg hover:shadow-green-600/30" data-testid="consultor-hero-whatsapp">
                  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    Contactar por WhatsApp
                  </a>
                </Button>
              </motion.div>
              <motion.p variants={fadeInUp} className="text-slate-500 text-sm mt-4 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Respuesta en menos de 24 horas
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Qué hace un consultor de datos */}
        <section className="relative section-padding bg-slate-900/50" data-testid="consultor-que-hace">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="consultor-h2-1">
                  Qué hace un <span className="text-gradient">analista de datos</span> en una empresa
                </h2>
                <p className="text-slate-400 max-w-3xl mx-auto text-base md:text-lg leading-relaxed">
                  Un analista de datos en Murcia revisa la información que tu empresa ya genera para
                  encontrar patrones, ineficiencias y oportunidades que a simple vista no se ven.
                  No se trata de instalar software ni de generar más informes, sino de convertir
                  números en respuestas concretas que permitan tomar mejores decisiones de negocio.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { icon: <Search className="w-7 h-7" />, title: "Diagnosticar", desc: "Identifico dónde están los puntos ciegos de tu negocio: qué datos tienes, qué te falta y qué no estás aprovechando." },
                  { icon: <Brain className="w-7 h-7" />, title: "Analizar", desc: "Convierto datos dispersos en información útil. Cruzo fuentes, detecto patrones y establezco relaciones que revelan lo que realmente está pasando." },
                  { icon: <Target className="w-7 h-7" />, title: "Recomendar", desc: "Traduzco los hallazgos en acciones concretas. No entrego informes genéricos, sino recomendaciones específicas para tu situación." }
                ].map((item, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <Card className="h-full bg-slate-900 border-slate-800 card-hover" data-testid={`consultor-que-hace-${i}`}>
                      <CardContent className="p-6">
                        <div className="w-12 h-12 rounded-xl bg-indigo-600/20 flex items-center justify-center text-indigo-400 mb-4">
                          {item.icon}
                        </div>
                        <h3 className="font-semibold text-white mb-2 text-lg">{item.title}</h3>
                        <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Cómo puedo ayudarte */}
        <section className="relative section-padding overflow-hidden" data-testid="consultor-como-ayudo">
          <div className="spotlight-1 top-0 right-0" />
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="consultor-h2-2">
                  Cómo puedo <span className="text-gradient">ayudarte en tu empresa</span>
                </h2>
                <p className="text-slate-400 max-w-3xl text-base md:text-lg leading-relaxed">
                  Cada empresa es diferente. No aplico plantillas ni soluciones estándar. Trabajo contigo
                  para entender tu negocio y encontrar exactamente dónde están las oportunidades de mejora.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 gap-6">
                {[
                  { icon: <TrendingUp className="w-6 h-6" />, title: "Detectar fugas de rentabilidad", desc: "Muchas empresas facturan bien pero sus márgenes reales son menores de lo que creen. Analizo costes, precios y operaciones para encontrar dónde se escapa el beneficio." },
                  { icon: <Users className="w-6 h-6" />, title: "Conocer mejor a tus clientes", desc: "Identifico qué clientes son realmente rentables, cuáles repiten, cuáles se van y por qué. Información clave para enfocar tus esfuerzos comerciales." },
                  { icon: <BarChart3 className="w-6 h-6" />, title: "Medir lo que funciona", desc: "Si inviertes en marketing, en personal o en nuevos productos, necesitas saber si está dando resultados. Construyo los indicadores que te permiten saberlo con certeza." },
                  { icon: <Lightbulb className="w-6 h-6" />, title: "Encontrar oportunidades ocultas", desc: "Los datos suelen revelar oportunidades que pasan desapercibidas: segmentos de mercado sin explotar, productos con potencial o procesos que se pueden optimizar." }
                ].map((item, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <div className="glass-card rounded-xl p-6 h-full" data-testid={`consultor-ayuda-${i}`}>
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-indigo-600/20 flex items-center justify-center text-indigo-400 flex-shrink-0 mt-1">
                          {item.icon}
                        </div>
                        <div>
                          <h3 className="font-semibold text-white mb-2">{item.title}</h3>
                          <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Problemas habituales */}
        <section className="relative section-padding bg-slate-900/50" data-testid="consultor-problemas">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="consultor-h2-3">
                  Problemas que ayudo a detectar en <span className="text-gradient">empresas de Murcia</span>
                </h2>
                <p className="text-slate-400 max-w-3xl mx-auto text-base leading-relaxed">
                  Después de trabajar con empresas de la Región de Murcia, estos son los problemas
                  que más se repiten. Si te identificas con alguno, probablemente tienes margen de mejora.
                </p>
              </motion.div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  "Toman decisiones importantes basándose solo en la experiencia o la intuición",
                  "Tienen datos de ventas, clientes y costes dispersos en diferentes sistemas sin conexión",
                  "Generan informes a final de mes que nadie usa para tomar decisiones",
                  "No saben qué clientes o productos son realmente rentables una vez descontados todos los costes",
                  "Invierten en campañas de marketing sin poder medir el retorno real de cada acción",
                  "Dedican horas a procesos manuales que podrían automatizarse con las herramientas adecuadas"
                ].map((problem, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <Card className="h-full bg-slate-900 border-slate-800 card-hover" data-testid={`consultor-problema-${i}`}>
                      <CardContent className="p-6 flex items-start gap-4">
                        <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-red-400 text-sm font-bold">{i + 1}</span>
                        </div>
                        <p className="text-slate-300 text-sm leading-relaxed">{problem}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Servicios que ofrezco */}
        <section className="relative section-padding overflow-hidden" data-testid="consultor-servicios">
          <div className="spotlight-2 -bottom-20 -left-20" />
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="consultor-h2-4">
                  Servicios que <span className="text-gradient">ofrezco</span>
                </h2>
                <p className="text-slate-400 max-w-3xl mx-auto text-base leading-relaxed">
                  Cada servicio se adapta a las necesidades reales de tu empresa. No hay paquetes cerrados
                  ni soluciones genéricas.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 gap-8">
                {[
                  {
                    icon: <Search className="w-7 h-7" />,
                    title: "Análisis de datos",
                    desc: "Recojo, limpio y analizo los datos de tu negocio para extraer conclusiones accionables. Desde análisis de ventas hasta segmentación de clientes o detección de anomalías.",
                    features: ["Análisis de ventas y rentabilidad", "Segmentación de clientes", "Detección de patrones y tendencias"]
                  },
                  {
                    icon: <BarChart3 className="w-7 h-7" />,
                    title: "Dashboards e informes visuales",
                    desc: "Creo paneles interactivos donde puedes ver el estado de tu negocio en tiempo real. Sin necesidad de abrir hojas de cálculo ni pedir informes a nadie.",
                    features: ["Cuadros de mando personalizados", "KPIs en tiempo real", "Acceso desde cualquier dispositivo"]
                  },
                  {
                    icon: <Cog className="w-7 h-7" />,
                    title: "Automatización de procesos",
                    desc: "Elimino tareas repetitivas que consumen tiempo y recursos. Automatizo la generación de informes, la carga de datos y los flujos de trabajo manuales.",
                    features: ["Informes automáticos", "Integración de sistemas", "Eliminación de trabajo manual"]
                  },
                  {
                    icon: <LineChart className="w-7 h-7" />,
                    title: "Business Intelligence",
                    desc: "Implemento soluciones de BI completas que centralizan toda la información de tu empresa y la convierten en un activo estratégico para la toma de decisiones.",
                    features: ["Centralización de datos", "Modelos predictivos", "Alertas y seguimiento automático"]
                  }
                ].map((service, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <Card className="h-full bg-slate-900 border-slate-800 card-hover" data-testid={`consultor-servicio-${i}`}>
                      <CardContent className="p-6 md:p-8">
                        <div className="w-12 h-12 rounded-xl bg-indigo-600/20 flex items-center justify-center text-indigo-400 mb-4">
                          {service.icon}
                        </div>
                        <h3 className="font-bold text-white text-lg mb-3">{service.title}</h3>
                        <p className="text-slate-400 text-sm leading-relaxed mb-4">{service.desc}</p>
                        <ul className="space-y-2">
                          {service.features.map((f, j) => (
                            <li key={j} className="text-slate-300 text-sm flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                              {f}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>

              <motion.div variants={fadeInUp} className="text-center mt-8">
                <Link to="/servicios" className="text-indigo-400 hover:text-indigo-300 text-sm font-medium inline-flex items-center gap-2 transition-colors" data-testid="consultor-link-servicios">
                  Ver todos los servicios en detalle <ChevronRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Casos de uso reales */}
        <section className="relative section-padding bg-slate-900/50" data-testid="consultor-casos">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="consultor-h2-5">
                  Casos de uso <span className="text-gradient">reales</span>
                </h2>
                <p className="text-slate-400 max-w-3xl mx-auto text-base leading-relaxed">
                  Ejemplos prácticos de cómo el análisis de datos resuelve problemas concretos
                  en empresas reales.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-3 gap-6">
                {[
                  {
                    icon: <ShoppingCart className="w-6 h-6" />,
                    sector: "Comercio",
                    title: "Optimización de inventario",
                    desc: "Una empresa de distribución tenía un 22% de stock parado que generaba costes de almacenamiento innecesarios. Analizamos la rotación por producto y temporada, ajustamos las compras y redujimos el stock muerto a menos del 8%."
                  },
                  {
                    icon: <Building className="w-6 h-6" />,
                    sector: "Servicios",
                    title: "Rentabilidad por cliente",
                    desc: "Una empresa de servicios profesionales descubrió que el 30% de sus clientes generaban el 80% de los beneficios. Reorientamos la estrategia comercial hacia los perfiles más rentables y mejoramos el margen neto un 15%."
                  },
                  {
                    icon: <TrendingUp className="w-6 h-6" />,
                    sector: "Marketing",
                    title: "Retorno de inversión publicitaria",
                    desc: "Una PYME invertía en cinco canales de marketing diferentes sin saber cuál funcionaba mejor. Implementamos un sistema de atribución que reveló que el 60% del presupuesto iba a canales con retorno negativo."
                  }
                ].map((caso, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <Card className="h-full bg-slate-900 border-slate-800 card-hover" data-testid={`consultor-caso-${i}`}>
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 rounded-lg bg-indigo-600/20 flex items-center justify-center text-indigo-400">
                            {caso.icon}
                          </div>
                          <span className="text-xs font-medium text-indigo-400 bg-indigo-400/10 px-3 py-1 rounded-full">
                            {caso.sector}
                          </span>
                        </div>
                        <h3 className="font-semibold text-white mb-3">{caso.title}</h3>
                        <p className="text-slate-400 text-sm leading-relaxed">{caso.desc}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>

              <motion.div variants={fadeInUp} className="text-center mt-8">
                <Link to="/analisis-datos-empresas" className="text-indigo-400 hover:text-indigo-300 text-sm font-medium inline-flex items-center gap-2 transition-colors" data-testid="consultor-link-analisis">
                  Conoce más sobre análisis de datos para empresas <ChevronRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Beneficios */}
        <section className="relative section-padding overflow-hidden" data-testid="consultor-beneficios">
          <div className="spotlight-1 -top-20 right-0" />
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="consultor-h2-6">
                  Beneficios de trabajar con un <span className="text-gradient">consultor de datos</span>
                </h2>
              </motion.div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: <Target className="w-6 h-6" />, title: "Decisiones basadas en hechos", desc: "Dejas de decidir por intuición y empiezas a actuar con información real sobre lo que funciona y lo que no." },
                  { icon: <Zap className="w-6 h-6" />, title: "Resultados rápidos", desc: "No hacen falta proyectos de meses. Desde la primera semana puedes tener datos útiles sobre tu negocio." },
                  { icon: <Settings className="w-6 h-6" />, title: "Sin complicaciones técnicas", desc: "Me encargo de toda la parte técnica. Tú recibes las conclusiones y las recomendaciones, listas para aplicar." },
                  { icon: <TrendingUp className="w-6 h-6" />, title: "Impacto directo en resultados", desc: "Cada análisis está orientado a mejorar algo concreto: reducir costes, aumentar ventas o mejorar márgenes." }
                ].map((benefit, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <Card className="h-full bg-slate-900 border-slate-800 card-hover text-center" data-testid={`consultor-beneficio-${i}`}>
                      <CardContent className="p-6">
                        <div className="w-12 h-12 rounded-xl bg-indigo-600/20 flex items-center justify-center text-indigo-400 mb-4 mx-auto">
                          {benefit.icon}
                        </div>
                        <h3 className="font-semibold text-white mb-2">{benefit.title}</h3>
                        <p className="text-slate-400 text-sm leading-relaxed">{benefit.desc}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Proceso de trabajo */}
        <section className="relative section-padding bg-slate-900/50" data-testid="consultor-proceso">
          <div className="max-w-4xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="consultor-h2-7">
                  Proceso de <span className="text-gradient">trabajo</span>
                </h2>
                <p className="text-slate-400 max-w-2xl mx-auto text-base leading-relaxed">
                  Un proceso claro, sin sorpresas y orientado a resultados desde el primer día.
                </p>
              </motion.div>

              <div className="space-y-6">
                {[
                  { step: "01", title: "Revisión inicial gratuita", desc: "Hablamos sobre tu negocio, tus objetivos y los datos que tienes disponibles. Sin compromiso. Si puedo ayudarte, te explico cómo. Si no, te lo digo directamente." },
                  { step: "02", title: "Diagnóstico y propuesta", desc: "Analizo tu situación actual, identifico las oportunidades más relevantes y te presento una propuesta concreta con alcance, tiempos y coste definidos." },
                  { step: "03", title: "Análisis y desarrollo", desc: "Trabajo con tus datos para construir los análisis, dashboards o automatizaciones acordadas. Te mantengo informado del progreso en todo momento." },
                  { step: "04", title: "Entrega y formación", desc: "Te presento los resultados, te explico las conclusiones y te enseño a utilizar las herramientas creadas. Todo queda documentado y listo para usar." },
                  { step: "05", title: "Seguimiento", desc: "Después de la entrega, reviso contigo los resultados y ajusto lo que sea necesario para asegurar que el proyecto genera el impacto esperado." }
                ].map((step, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <div className="glass-card rounded-xl p-6 flex items-start gap-6" data-testid={`consultor-paso-${i}`}>
                      <div className="text-3xl font-extrabold text-indigo-400/30 flex-shrink-0 leading-none">
                        {step.step}
                      </div>
                      <div>
                        <h3 className="font-semibold text-white mb-2">{step.title}</h3>
                        <p className="text-slate-400 text-sm leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* FAQ */}
        <section className="relative section-padding overflow-hidden" data-testid="consultor-faq">
          <div className="max-w-3xl mx-auto relative z-10">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="consultor-h2-8">
                  Preguntas <span className="text-gradient">frecuentes</span>
                </h2>
              </motion.div>

              <div className="space-y-4">
                {FAQS.map((faq, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <Card className="bg-slate-900 border-slate-800" data-testid={`consultor-faq-${i}`}>
                      <CardContent className="p-6">
                        <h3 className="font-semibold text-white mb-3 flex items-start gap-3">
                          <HelpCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                          {faq.q}
                        </h3>
                        <p className="text-slate-400 text-sm leading-relaxed pl-8">{faq.a}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* SEO Content Block - Texto continuo */}
        <section className="relative section-padding" data-testid="consultor-seo-content">
          <div className="max-w-4xl mx-auto">
            <div className="text-slate-400 text-base leading-relaxed space-y-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">Análisis de datos para empresas en Murcia: un enfoque práctico</h2>

              <p>
                Cuando una empresa me contacta como analista de datos en Murcia, normalmente es porque tiene la sensación de que algo no funciona del todo bien, pero no sabe exactamente dónde está el problema. Puede ser que facturen bien pero el margen sea menor de lo esperado. O que tengan clientes que compran mucho pero no dejan beneficio real. O que el stock esté descontrolado y nadie sepa por qué.
              </p>

              <p>
                El análisis de datos para empresas consiste precisamente en eso: coger la información que ya existe en el negocio — ventas, costes, clientes, operaciones — y convertirla en respuestas claras. No hacen falta proyectos largos ni herramientas caras. Lo que hace falta es alguien que sepa leer los números y traducirlos en decisiones que impacten en la cuenta de resultados.
              </p>

              <h3 className="text-xl font-bold text-white mt-8 mb-4">Cómo trabajo con los datos de tu empresa</h3>

              <p>
                Mi enfoque como consultor de datos en Murcia es siempre práctico. No hago dashboards por hacer dashboards, ni automatizo cosas que no necesitan automatizarse. Empiezo por entender tu negocio, identificar los problemas reales y trabajar con los datos que ya tienes para encontrar soluciones concretas. Si hay que crear un cuadro de mando, lo creo. Pero solo si aporta valor real. Cuando el proyecto requiere más trabajo de campo —limpieza, modelado y análisis intensivo—, asumo también el rol de <Link to="/analista-datos-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">analista de datos en Murcia</Link>.
              </p>

              <p>
                Trabajo con herramientas como Power BI para visualizar datos de forma clara, SQL para acceder y transformar grandes volúmenes de información, y Python para automatizar procesos y construir modelos predictivos cuando tiene sentido. No se trata de la herramienta sino de resolver el problema.
              </p>

              <h3 className="text-xl font-bold text-white mt-8 mb-4">Beneficios reales para tu empresa</h3>

              <p>
                Las empresas con las que trabajo en Murcia y en toda España consiguen resultados medibles: mejor rentabilidad porque detectamos dónde se pierde margen, reducción de costes porque identificamos gastos innecesarios, decisiones más rápidas porque la información está accesible y clara, y mayor control del negocio porque los datos dejan de ser un problema y pasan a ser una ventaja competitiva.
              </p>

              <p>
                Si buscas un analista de datos en Murcia que te ayude a mejorar los resultados de tu empresa sin complicaciones, podemos empezar con un diagnóstico rápido y gratuito. Puedes ver también nuestros servicios de <Link to="/business-intelligence-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">Business Intelligence en Murcia</Link> o conocer más sobre nuestro <Link to="/analisis-datos-empresas" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">servicio de análisis de datos para empresas</Link>. También puedes leer nuestro artículo sobre <Link to="/blog/como-saber-si-tu-empresa-pierde-dinero" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">cómo saber si tu empresa está perdiendo dinero</Link>.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="relative section-padding bg-gradient-to-b from-slate-900/80 to-[#020617]" data-testid="consultor-cta-final">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
              <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl font-bold mb-4" data-testid="consultor-h2-9">
                ¿Quieres saber dónde pierde dinero <span className="text-gradient">tu empresa</span>?
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-slate-400 mb-8 max-w-xl mx-auto text-base leading-relaxed">
                Si quieres entender mejor qué está pasando en tu empresa y tomar decisiones basadas en datos reales,
                contacta para un diagnóstico inicial gratuito. En 7 días te digo exactamente dónde estás perdiendo dinero
                y qué puedes hacer para recuperarlo.
              </motion.p>

              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                <Button asChild className="btn-glow bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-base rounded-full" data-testid="consultor-cta-contact">
                  <Link to="/contacto">
                    Solicitar revisión gratuita
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-base rounded-full transition-all hover:scale-105 hover:shadow-lg hover:shadow-green-600/30" data-testid="consultor-cta-whatsapp">
                  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    Escríbenos por WhatsApp
                  </a>
                </Button>
              </motion.div>

              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-6 text-slate-400 text-sm">
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-indigo-400" />
                  Respuesta en menos de 24 horas
                </span>
                <span className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-indigo-400" />
                  <a href="mailto:info@datyon.com" className="hover:text-white transition-colors">info@datyon.com</a>
                </span>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-800" data-testid="consultor-footer">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="DATYON" className="h-8 w-auto" />
              <div className="flex items-center gap-2 text-slate-400">
                <MapPin className="w-4 h-4 text-indigo-400" />
                <span className="text-sm">Murcia, España</span>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-slate-500 hover:text-white text-sm transition-colors">Inicio</Link>
              <Link to="/servicios" className="text-slate-500 hover:text-white text-sm transition-colors">Servicios</Link>
              <Link to="/blog" className="text-slate-500 hover:text-white text-sm transition-colors">Blog</Link>
              <Link to="/contacto" className="text-slate-500 hover:text-white text-sm transition-colors">Contacto</Link>
            </div>
          </div>
          <div className="flex flex-wrap justify-center md:justify-start gap-4 mb-6 pt-4 border-t border-slate-800">
            <Link to="/aviso-legal" className="text-slate-500 hover:text-white text-sm transition-colors">Aviso Legal</Link>
            <span className="text-slate-700">|</span>
            <Link to="/politica-privacidad" className="text-slate-500 hover:text-white text-sm transition-colors">Política de Privacidad</Link>
            <span className="text-slate-700">|</span>
            <Link to="/politica-cookies" className="text-slate-500 hover:text-white text-sm transition-colors">Política de Cookies</Link>
          </div>
          <p className="text-slate-500 text-sm text-center md:text-left">
            © {new Date().getFullYear()} DATYON - Antonio Oliver. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* WhatsApp Floating */}
      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg shadow-green-500/30 transition-all duration-300 hover:scale-110 group"
        data-testid="consultor-whatsapp-float" aria-label="Contactar por WhatsApp">
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-slate-800 text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          ¿Hablamos?
        </span>
      </a>

      <CookieBanner />
    </div>
  );
}
